import { Bot, MapPin } from "lucide-react";
import { useEffect } from "react";

import { Badge } from "@/components/ui/badge";
import { BottomNav } from "@/components/layout/BottomNav";
import { DeviceFrame } from "@/components/layout/DeviceFrame";
import { TopAppBar } from "@/components/layout/TopAppBar";
import { providerTabHandler } from "@/lib/nav";
import type { Route } from "@/lib/router";
import { useApp } from "@/state/AppContext";

interface ProviderExploreProps {
  navigate: (route: Route) => void;
}

/** Nearby incoming requests — orders in pending_approval for this business. */
export function ProviderExplore({ navigate }: ProviderExploreProps) {
  const { jobs, businessId, agentActive, refreshProviderOrders, showToast } = useApp();
  const onTab = providerTabHandler(navigate, showToast);

  useEffect(() => {
    void refreshProviderOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const incoming = jobs.filter(j => j.status === "pending");

  return (
    <DeviceFrame>
      <TopAppBar
        title={<h1 className="text-lg font-bold tracking-tight text-primary">Explore</h1>}
        right={
          <span className="flex size-9 items-center justify-center rounded-full border border-deep-slate/10 bg-surface-variant text-[11px] font-semibold text-on-surface-variant">
            {businessId?.slice(0, 2).toUpperCase() ?? "··"}
          </span>
        }
      />

      <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-24 pt-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-primary">Nearby Requests</h1>
          <p className="mt-1 text-[15px] text-on-surface-variant">
            Customers looking for what you offer, matched to your area.
          </p>
        </div>

        <div className="flex items-center gap-3 rounded-lg border border-secondary/20 bg-secondary/5 p-3.5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary/15 text-secondary">
            <Bot className="size-5" fill="currentColor" />
          </span>
          <p className="text-[13px] leading-5 text-on-surface-variant">
            {agentActive
              ? "Your AI agent compares incoming requests and replies with fixed prices."
              : "Your AI agent is off — you'll handle these manually."}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {incoming.length === 0 ? (
            <p className="rounded-lg border border-deep-slate/10 bg-white p-4 text-[14px] text-on-surface-variant">
              No new requests right now.
            </p>
          ) : (
            incoming.map(request => (
              <button
                key={request.id}
                onClick={() => navigate("provider")}
                className="flex flex-col gap-2 rounded-lg border border-secondary/40 bg-white p-4 text-left transition-colors hover:border-secondary"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[16px] font-bold text-primary">{request.customer}</p>
                    <p className="mt-0.5 truncate text-[14px] text-on-surface-variant">{request.item}</p>
                  </div>
                  <Badge variant="mint">New</Badge>
                </div>
                <div className="flex items-center gap-3 font-mono text-[12px] text-on-surface-variant">
                  <span className="flex items-center gap-1">
                    <MapPin className="size-3.5" />
                    {request.location}
                  </span>
                  <span className="rounded-sm bg-surface-container-low px-1.5 py-0.5">{request.price.toLocaleString()} PKR</span>
                </div>
              </button>
            ))
          )}
        </div>
      </main>

      <div className="absolute inset-x-0 bottom-0 z-30">
        <BottomNav variant="provider" active="products" onSelect={onTab} />
      </div>
    </DeviceFrame>
  );
}
